/**
 * Filterable Mixin
 *
 * Provides an extensible filter system for Vue components
 * Uses a filter registry pattern to allow easy addition of new filters
 */

export const filterable = {
  data() {
    return {
      activeFilters: {},
      filterRegistry: {},
      currentFilterType: 'all'
    };
  },

  computed: {
    /**
     * Check if any filters are active
     */
    hasActiveFilters() {
      return this.currentFilterType !== 'all' || Object.keys(this.activeFilters).some(
        key => {
          const value = this.activeFilters[key];
          // Check if filter has a meaningful value
          if (Array.isArray(value)) return value.length > 0;
          if (typeof value === 'boolean') return value;
          return value !== null && value !== undefined && value !== '';
        }
      );
    },

    /**
     * Count of active filters
     */
    activeFilterCount() {
      let count = 0;
      if (this.currentFilterType !== 'all') count++;

      Object.keys(this.activeFilters).forEach(key => {
        const value = this.activeFilters[key];
        if (Array.isArray(value) && value.length > 0) count++;
        else if (typeof value === 'boolean' && value) count++;
        else if (value !== null && value !== undefined && value !== '') count++;
      });

      return count;
    }
  },

  methods: {
    /**
     * Register a new filter
     * @param {string} name - Unique filter name
     * @param {Function} filterFn - Filter function that takes (items, filterValue) and returns filtered items
     */
    registerFilter(name, filterFn) {
      this.$set(this.filterRegistry, name, filterFn);
    },

    /**
     * Apply a filter
     * @param {string} filterName - Name of the registered filter
     * @param {*} value - Filter value
     */
    applyFilter(filterName, value) {
      this.$set(this.activeFilters, filterName, value);
      this.saveFilterState();
    },

    /**
     * Remove a specific filter
     * @param {string} filterName - Name of the filter to remove
     */
    removeFilter(filterName) {
      this.$delete(this.activeFilters, filterName);
      this.saveFilterState();
    },

    /**
     * Clear all filters
     */
    clearAllFilters() {
      this.activeFilters = {};
      this.currentFilterType = 'all';
      this.saveFilterState();
    },

    /**
     * Apply all registered filters to a dataset
     * @param {Array} items - Items to filter
     * @returns {Array} Filtered items
     */
    applyAllFilters(items) {
      let filtered = items;

      // Apply each active filter
      Object.keys(this.activeFilters).forEach(filterName => {
        const filterFn = this.filterRegistry[filterName];
        const filterValue = this.activeFilters[filterName];

        if (filterFn && this.isFilterValueValid(filterValue)) {
          filtered = filterFn(filtered, filterValue);
        }
      });

      return filtered;
    },

    /**
     * Check if a filter value is valid (not empty/null/undefined)
     * @param {*} value - Filter value to check
     * @returns {boolean}
     */
    isFilterValueValid(value) {
      if (value === null || value === undefined) return false;
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'string') return value.trim().length > 0;
      if (typeof value === 'boolean') return true;
      return true;
    },

    /**
     * Save filter state to sessionStorage
     */
    saveFilterState() {
      try {
        sessionStorage.setItem('layerFilters', JSON.stringify({
          activeFilters: this.activeFilters,
          currentFilterType: this.currentFilterType
        }));
      } catch (e) {
        console.warn('Could not save filter state:', e);
      }
    },

    /**
     * Restore filter state from sessionStorage
     */
    restoreFilterState() {
      try {
        const savedFilters = sessionStorage.getItem('layerFilters');
        if (savedFilters) {
          const parsed = JSON.parse(savedFilters);
          this.activeFilters = parsed.activeFilters || {};
          this.currentFilterType = parsed.currentFilterType || 'all';
        }
      } catch (e) {
        console.warn('Could not restore filter state:', e);
      }
    },

    /**
     * Get a summary of active filters
     * @returns {Array} Array of active filter descriptions
     */
    getActiveFiltersSummary() {
      const summary = [];

      if (this.currentFilterType !== 'all') {
        summary.push({
          type: 'filterType',
          label: `Filter: ${this.currentFilterType}`
        });
      }

      Object.keys(this.activeFilters).forEach(key => {
        const value = this.activeFilters[key];
        if (this.isFilterValueValid(value)) {
          summary.push({
            type: key,
            label: this.getFilterLabel(key, value)
          });
        }
      });

      return summary;
    },

    /**
     * Get a human-readable label for a filter
     * Override this in your component for custom labels
     * @param {string} filterName - Filter name
     * @param {*} filterValue - Filter value
     * @returns {string}
     */
    getFilterLabel(filterName, filterValue) {
      if (Array.isArray(filterValue)) {
        return `${filterName}: ${filterValue.length} selected`;
      }
      return `${filterName}: ${filterValue}`;
    }
  },

  mounted() {
    // Restore filter state when component mounts
    this.restoreFilterState();
  }
};
